import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as log from '../util/log';
import { storableError } from '../util/errors';
import { createLastActivityTimestamp, encryptString } from '../util/authHelpers';
import { recordReferralPriorActivity } from '../util/tapfiliate';
import {
  createSignupNameValidationApiError,
  validateSignupEmailField,
  validateSignupNameFields,
} from '../util/signupNameValidation';
import {
  createSignupCaptchaApiError,
  executeSignupCaptcha,
  isSignupCaptchaEnabled,
  SIGNUP_CAPTCHA_ACTION,
  SIGNUP_CAPTCHA_ERROR_FAILED,
} from '../util/captcha';
import { clearCurrentUser, fetchCurrentUser } from './user.duck';
import { createUser, createUserWithIdp } from '../util/api';
import { StatusCode } from '../util/enums';

const authenticated = authInfo => authInfo?.isAnonymous === false;
const loggedInAs = authInfo => authInfo?.isLoggedInAs === true;
const asStorableError = error => (error?.apiErrors ? error : storableError(error));

const withCaptchaTokenIfNeeded = params => {
  if (!isSignupCaptchaEnabled()) {
    return Promise.resolve(params);
  }

  if (params?.captchaToken) {
    return Promise.resolve(params);
  }

  return executeSignupCaptcha({ action: SIGNUP_CAPTCHA_ACTION })
    .then(captchaToken => {
      if (!captchaToken) {
        return Promise.reject(createSignupCaptchaApiError(SIGNUP_CAPTCHA_ERROR_FAILED));
      }

      return {
        ...params,
        captchaToken,
      };
    })
    .catch(error => {
      if (error?.apiErrors) {
        return Promise.reject(storableError(error));
      }
      return Promise.reject(
        storableError(createSignupCaptchaApiError(SIGNUP_CAPTCHA_ERROR_FAILED))
      );
    });
};

// ================ Initial State ================ //

const initialState = {
  isAuthenticated: false,

  // is marketplace operator logged in as a marketplace user
  isLoggedInAs: false,

  // scopes associated with current token
  authScopes: [],

  // auth info
  authInfoLoaded: false,

  // login
  loginError: null,
  loginInProgress: false,

  // logout
  logoutError: null,
  logoutInProgress: false,

  // signup
  signupError: null,
  signupInProgress: false,

  // confirm (create use with idp)
  confirmError: null,
  confirmInProgress: false,
};

// ================ Async Thunks ================ //

const authInfoThunk = createAsyncThunk('auth/authInfo', (_, thunkAPI) => {
  const { extra: sdk } = thunkAPI;
  return sdk.authInfo().catch(e => {
    // Requesting auth info just reads the token from the token
    // store (i.e. cookies), and should not fail in normal
    // circumstances. If it fails, it's due to a programming
    // error. In that case we mark the operation done and dispatch
    // `null` success action that marks the user as unauthenticated.
    log.error(e, 'auth-info-failed');
    return null;
  });
});

const loginThunk = createAsyncThunk(
  'auth/login',
  ({ username, password }, thunkAPI) => {
    const { rejectWithValue, extra: sdk, dispatch, getState } = thunkAPI;

    return sdk
      .login({ username, password })
      .then(() => {
        return dispatch(fetchCurrentUser({ afterLogin: true }));
      })
      .then(() => {
        const currentUser = getState()?.user?.currentUser;
        const publicData = currentUser?.attributes?.profile?.publicData || {};
        const privateData = currentUser?.attributes?.profile?.privateData || {};
        // Snapshot prior lastActivity for affiliate eligibility BEFORE we
        // overwrite it below (no-op unless an affiliate referral is pending).
        recordReferralPriorActivity(currentUser);
        const identity = encryptString(password);
        const updateProfile = sdk?.currentUser?.updateProfile;

        if (typeof updateProfile !== 'function') {
          return null;
        }

        const MAX_RETRIES = 2;
        const updateWithRetry = (payload, retriesLeft) => {
          return updateProfile(payload).catch(e => {
            const isConflict = e?.status === StatusCode.CONFLICT;
            if (isConflict && retriesLeft > 0) {
              // Wait a bit and retry to resolve the optimistic locking conflict
              return new Promise(resolve => setTimeout(resolve, 500)).then(() =>
                updateWithRetry(payload, retriesLeft - 1)
              );
            }
            throw e;
          });
        };

        return updateWithRetry(
          {
            publicData: {
              ...publicData,
              lastActivity: createLastActivityTimestamp(),
            },
            privateData: {
              ...privateData,
              ...(identity ? { identity } : {}),
            },
          },
          MAX_RETRIES
        ).catch(e => {
          // Non-blocking profile sync on login. Login success should not fail because of this.
          // 409 conflict is a transient race condition (concurrent profile update) — not actionable.
          if (e?.status !== StatusCode.CONFLICT) {
            log.error(e, 'login-profile-sync-failed', { username });
          }
          return null;
        });
      })
      .then(() => ({ username, password }))
      .catch(e => rejectWithValue(storableError(e)));
  },
  {
    condition: (_, { getState }) => {
      const state = getState();
      if (authenticationInProgress(state, 'loginInProgress')) {
        return false;
      }
    },
  }
);

const logoutThunk = createAsyncThunk(
  'auth/logout',
  (_, thunkAPI) => {
    const { rejectWithValue, extra: sdk, dispatch } = thunkAPI;

    return sdk
      .logout()
      .then(() => {
        dispatch(clearCurrentUser());
        log.clearUserId();
        return true;
      })
      .catch(e => rejectWithValue(storableError(e)));
  },
  {
    condition: (_, { getState }) => {
      const state = getState();
      if (authenticationInProgress(state, 'logoutInProgress')) {
        return false;
      }
    },
  }
);

const signupThunk = createAsyncThunk(
  'auth/signup',
  (params, thunkAPI) => {
    const { rejectWithValue, extra: sdk, dispatch } = thunkAPI;
    const signupParams = params || {};
    const emailValidationIssue = validateSignupEmailField(signupParams?.email);
    if (emailValidationIssue) {
      return rejectWithValue(
        storableError(createSignupNameValidationApiError(emailValidationIssue))
      );
    }

    const nameValidationIssue = validateSignupNameFields({
      firstName: signupParams?.firstName,
      lastName: signupParams?.lastName,
    });

    if (nameValidationIssue) {
      return rejectWithValue(
        storableError(createSignupNameValidationApiError(nameValidationIssue))
      );
    }

    return withCaptchaTokenIfNeeded(signupParams)
      .then(protectedSignupParams => createUser(protectedSignupParams))
      .then(() =>
        dispatch(
          loginThunk({ username: signupParams.email, password: signupParams.password })
        ).unwrap()
      )
      .then(() => signupParams)
      .catch(e => {
        const status = e?.status || e?.statusCode || e?.response?.status || null;
        if (status !== StatusCode.CONFLICT) {
          log.error(e, 'signup-failed', {
            email: signupParams.email,
            firstName: signupParams.firstName,
            lastName: signupParams.lastName,
          });
        }
        return rejectWithValue(asStorableError(e));
      });
  },
  {
    condition: (_, { getState }) => {
      const state = getState();
      if (authenticationInProgress(state, 'signupInProgress')) {
        return false;
      }
    },
  }
);

const signupWithIdpThunk = createAsyncThunk(
  'auth/signupWithIdp',
  (params, thunkAPI) => {
    const { rejectWithValue, dispatch } = thunkAPI;
    const idpSignupParams = params || {};
    const emailValidationIssue = validateSignupEmailField(idpSignupParams?.email);
    if (emailValidationIssue) {
      return rejectWithValue(
        storableError(createSignupNameValidationApiError(emailValidationIssue))
      );
    }

    const nameValidationIssue = validateSignupNameFields({
      firstName: idpSignupParams?.firstName,
      lastName: idpSignupParams?.lastName,
    });

    if (nameValidationIssue) {
      return rejectWithValue(
        storableError(createSignupNameValidationApiError(nameValidationIssue))
      );
    }

    return withCaptchaTokenIfNeeded(idpSignupParams)
      .then(protectedSignupParams => createUserWithIdp(protectedSignupParams))
      .then(() => dispatch(fetchCurrentUser({ afterLogin: true })))
      .then(() => idpSignupParams)
      .catch(e => {
        const status = e?.status || e?.statusCode || e?.response?.status || null;
        if (status !== StatusCode.CONFLICT) {
          log.error(e, 'create-user-with-idp-failed', { params: idpSignupParams });
        }
        return rejectWithValue(asStorableError(e));
      });
  },
  {
    condition: (_, { getState }) => {
      const state = getState();
      if (authenticationInProgress(state, 'confirmInProgress')) {
        return false;
      }
    },
  }
);

// ================ Slice ================ //

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {},
  extraReducers: builder => {
    // Auth Info
    builder.addCase(authInfoThunk.fulfilled, (state, action) => {
      const payload = action.payload;
      state.authInfoLoaded = true;
      state.isAuthenticated = authenticated(payload);
      state.isLoggedInAs = loggedInAs(payload);
      state.authScopes = payload?.scopes || [];
    });

    // Login
    builder
      .addCase(loginThunk.pending, state => {
        state.loginInProgress = true;
        state.loginError = null;
        state.logoutError = null;
        state.signupError = null;
      })
      .addCase(loginThunk.fulfilled, state => {
        state.loginInProgress = false;
        state.isAuthenticated = true;
      })
      .addCase(loginThunk.rejected, (state, action) => {
        state.loginInProgress = false;
        state.loginError = action.payload;
      });

    // Logout
    builder
      .addCase(logoutThunk.pending, state => {
        state.logoutInProgress = true;
        state.loginError = null;
        state.logoutError = null;
      })
      .addCase(logoutThunk.fulfilled, state => {
        state.logoutInProgress = false;
        state.isAuthenticated = false;
        state.isLoggedInAs = false;
        state.authScopes = [];
      })
      .addCase(logoutThunk.rejected, (state, action) => {
        state.logoutInProgress = false;
        state.logoutError = action.payload;
      });

    // Signup
    builder
      .addCase(signupThunk.pending, state => {
        state.signupInProgress = true;
        state.loginError = null;
        state.signupError = null;
      })
      .addCase(signupThunk.fulfilled, state => {
        state.signupInProgress = false;
      })
      .addCase(signupThunk.rejected, (state, action) => {
        state.signupInProgress = false;
        state.signupError = action.payload;
      });

    // Signup with IDP (Confirm)
    builder
      .addCase(signupWithIdpThunk.pending, state => {
        state.confirmInProgress = true;
        state.loginError = null;
        state.confirmError = null;
      })
      .addCase(signupWithIdpThunk.fulfilled, state => {
        state.confirmInProgress = false;
        state.isAuthenticated = true;
      })
      .addCase(signupWithIdpThunk.rejected, (state, action) => {
        state.confirmInProgress = false;
        state.confirmError = action.payload;
      });
  },
});

export { logoutThunk };
export default authSlice.reducer;

// ================ Selectors ================ //

export const authenticationInProgress = (state, nextInProgress = 'any') => {
  const { loginInProgress, logoutInProgress, signupInProgress, confirmInProgress } = state.auth;
  const anyInProgress =
    loginInProgress || logoutInProgress || signupInProgress || confirmInProgress;
  return nextInProgress === 'loginInProgress'
    ? loginInProgress || logoutInProgress || confirmInProgress
    : anyInProgress;
};

// ================ Thunk Wrappers ================ //
// These maintain the same API as the original thunks

export const login = (username, password) => dispatch => {
  return dispatch(loginThunk({ username, password })).unwrap();
};

export const logout = () => dispatch => {
  return dispatch(logoutThunk()).unwrap();
};

export const signup = params => dispatch => {
  return dispatch(signupThunk(params)).unwrap();
};

export const signupWithIdp = params => dispatch => {
  return dispatch(signupWithIdpThunk(params)).unwrap();
};

export const authInfo = () => dispatch => {
  return dispatch(authInfoThunk()).unwrap();
};
